// src/utils/PdfMakeUtils.js
import DOMPurify from 'dompurify';
import htmlToPdfMake from 'html-to-pdfmake';
import {
  DEFAULT_PDF_STYLES,
  INITIAL_MULTIPLIER_VALUE, LIST_BULLET_GAP, LIST_BULLET_SIZE, LIST_BULLET_TOP_OFFSET, LIST_NUMBER_GAP,
  MAX_PAGE_HEIGHT, MAX_PAGE_WIDTH, MERGED_HEADER_CELL_FILL_OPACITY,
  MINIMUM_CELL_WIDTH, PDF_SCALE_FACTOR, TABLE_COLS_DEFAULT_QUANTITY,
  TASK_CHECKBOX_COLUMN_WIDTH, TASK_CHECKBOX_GAP, TASK_CHECKBOX_SIZE, TASK_CHECKBOX_TOP_OFFSET,
  WIDTH_OF_TABLE_BORDER,
} from 'src/constants/pdf';

/**
   * Returns width and height of the provided image
   *
   * @param {string} src - image source
   * @returns {Promise} Promise object contains image and height of the image
   */
export function getImageDimensions(src) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    image.src = src;
  });
} // getImageDimensions()

/**
     * Goes deep through the element structure and fixes all margins converting them from String to Number
     *
     * @param itemSrc original element
     * @returns Object -  modified element with fixed margins
     * */
export function fixElementMargin(itemSrc) {
  let item = { ...itemSrc };
  if (item?.margin !== undefined) {
    item.margin = item.margin.map((value) => {
      return typeof value === 'string' && value.includes('px')
        ? parseInt(value.slice(0, value.indexOf('px')), 10)
        : value;
    });
  }

  if (Array.isArray(item) && item.length > 0) {
    item = item.map((el) => fixElementMargin(el));
  }

  if (Array.isArray(item.ul) && item.ul.length > 0) {
    item.ul = item.ul.map((listItem) => fixElementMargin(listItem));
  }

  if (Array.isArray(item.ol) && item.ol.length > 0) {
    item.ol = item.ol.map((listItem) => fixElementMargin(listItem));
  }

  if (Array.isArray(item.stack) && item.stack.length > 0) {
    item.stack = item.stack.map((stackItem) => fixElementMargin(stackItem));
  }

  if (Array.isArray(item.columns) && item.columns.length > 0) {
    item.columns = item.columns.map((column) => fixElementMargin(column));
  }

  if (Array.isArray(item.table?.body) && item.table.body.length > 0) {
    item.table.body = item.table.body.map((row) => row.map((rowItem) => fixElementMargin(rowItem)));
  }

  if (Array.isArray(item.text) && item.text.length > 0) {
    item.text = item.text.map((textItem) => fixElementMargin(textItem));
  }

  return item;
} // fixElementMargin()

export async function fixImagesAndMarginsForExport(content) {
  return Promise.all(
    content.map(async (originalItem) => {
      const item = { ...originalItem };

      if (item.nodeName === 'IMG' && item.image.includes('base64')) {
        // eslint-disable-next-line no-await-in-loop
        const imgDimensions = await getImageDimensions(item.image);

        // Page width (inside margins) - max width
        if (imgDimensions.width < MAX_PAGE_WIDTH) {
          item.width = imgDimensions.width; // set original width
        }

        // 800 - max height
        if (imgDimensions.height < MAX_PAGE_HEIGHT) {
          item.height = imgDimensions.height; // set original height
        }
      }

      return fixElementMargin(item);
    }),
  );
} // fixImagesAndMarginsForExport()

/**
 * Checks if all entries in an array have the same value for a given field or property.
 *
 * @param {Array} array - The array of items to check.
 * @param {Function} iteratee - A function that returns the value to compare for each item.
 * @returns {boolean} Returns true if all entries have the same value, otherwise false.
 */
function isSameFieldValueForArrayEntries(array, iteratee) {
  const values = array.map(iteratee);
  const uniqueValues = Array.from(new Set(values));
  return uniqueValues.length === 1;
} // isSameFieldValueForArrayEntries()

/**
 * Fixes case when users merge all cells in each column to achieve 1 row tables.
 *
 * @param {string} html
 * @return {string}
 */
export function fixMergedCells(html) {
  // @todo: move next line to somewhere else so the method does exactly what it says
  const sanitizedHtml = DOMPurify.sanitize(html, { ADD_ATTR: ['colwidth'] });
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, 'text/html');

  const tables = doc.querySelectorAll('table');

  // eslint-disable-next-line no-restricted-syntax
  for (const table of tables) {
    const rows = table.querySelectorAll('tr');
    let rowsNumberToDelete = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const row of rows) {
      const cells = Array.from(row.children).filter((el) => el.tagName === 'TH' || el.tagName === 'TD');

      if (cells.length > 0) {
        const rowspanValue = cells[0].getAttribute('rowspan');

        if (
          isSameFieldValueForArrayEntries(cells, (cell) => cell.getAttribute('rowspan'))
              && rowspanValue > 1
        ) {
          rowsNumberToDelete = rowspanValue - 1;

          cells.forEach((cell) => {
            cell.setAttribute('rowspan', 1);
          });
        }
      }

      if (cells.length === 0 && rowsNumberToDelete > 0) {
        row.remove();
        rowsNumberToDelete -= 1;
      }
    }
  }

  // Return only the content of the body
  return doc.body.innerHTML;
} // fixMergedCells()

/**
 * Fixes wide pdf table custom widths to fit page width.
 * Returns fixed element copy.
 *
 * @param {object} pdfPageContent
 * @return {object}
 */
function fixWideTableWidths(pdfPageContent) {
  const multiplier = MAX_PAGE_WIDTH / pdfPageContent.width;

  return {
    ...pdfPageContent,
    table: {
      ...pdfPageContent.table,
      widths: pdfPageContent.table.widths.map(
        (width) => (Number.isFinite(width) ? Math.round(width * multiplier) : width),
      ),
    },
    width: MAX_PAGE_WIDTH,
  };
} // fixWideTableWidths()

/**
 * Fixes paf table widths to make table columns auto-expandable.
 * Returns fixed element copy.
 *
 * @param {object} pdfPageContent
 * @return {object}
 */
function fixTableWidths(pdfPageContent) {
  // star-sign width - makes the column auto-expandable taking all free space in a row
  const defaultColWidth = '*';
  const rowWithMostColumns = pdfPageContent.table.body.reduce((rowWithMostCols, row) => (
    (row.length > rowWithMostCols.length)
      ? row
      : rowWithMostCols
  ), []);

  const tableWidths = rowWithMostColumns.map((col) => (
    (col.width < MAX_PAGE_WIDTH)
      ? col.width
      : defaultColWidth));

  // total width presented in widths array without a wildcard value. Sometimes it can be larger that MAX_PAGE_WIDTH
  // so we need to handle such case as well (e.g. widths=[150,*,420] in this case totalKnownWidth will be 570 and
  // such case should be treated as wide table as well)
  const totalKnownWidth = tableWidths.reduce((knownWidth, width) => (
    (width !== defaultColWidth)
      ? knownWidth + width
      : knownWidth
  ), 0);

  const pdfPageContentCopy = {
    ...pdfPageContent,
    table: {
      ...pdfPageContent.table,
      widths: tableWidths,
    },
  };

  const isPdfPageContentWiderThanMaxPageWidth = pdfPageContent.width > MAX_PAGE_WIDTH;
  const isTotalKnownWidthBiggerThanMaxPageWidth = totalKnownWidth > MAX_PAGE_WIDTH;
  const isTableTooWide = isPdfPageContentWiderThanMaxPageWidth || isTotalKnownWidthBiggerThanMaxPageWidth;
  if (isTableTooWide) {
    return fixWideTableWidths(pdfPageContentCopy);
  }

  return pdfPageContentCopy;
} // fixTableWidths()

/**
 * Calculates pdf element table cell auto width
 *
 * @param {(number | '*')[]} tableColsWidths - defines a width of each column in table
 * @return {number}
 */
function calcCellAutoWidth(tableColsWidths, noCellMargins = false) {
  if (!Array.isArray(tableColsWidths) || tableColsWidths.length === 0) {
    return MINIMUM_CELL_WIDTH;
  }

  const { totalCustomWidth, colsWithoutCustomWidth } = tableColsWidths.reduce(
    (acc, width) => {
      const isCustomWidth = width !== '*' && Number.isFinite(width);
      if (isCustomWidth) {
        acc.totalCustomWidth += width;
      } else {
        acc.colsWithoutCustomWidth += 1;
      }
      return acc;
    },
    { totalCustomWidth: 0, colsWithoutCustomWidth: 0 },
  );

  if (!colsWithoutCustomWidth) {
    return MINIMUM_CELL_WIDTH;
  }

  // NH: I have NO IDEA why 28. I have spent a whole day figuring out why the
  // original calculation from PdfMakeExportMixin was not working correctly here
  // and what it would take to fix it. But after many experiments I found out that
  // subtracting 28 from MAX_PAGE_WIDTH makes the calculation work as expected.
  // So here it is, a magic number to make things work.
  // If you are reading this and know why, please document it properly and remove
  // this comment and the magic number.
  // Update: It seems to be related to cell borders and margins, but still not 100% sure.
  const MAGIC_NUMBER = noCellMargins ? 0 : 28;

  const tableBordersWidthSubtractionMultiplier = INITIAL_MULTIPLIER_VALUE
        - (WIDTH_OF_TABLE_BORDER * (tableColsWidths.length - TABLE_COLS_DEFAULT_QUANTITY));

  const calculatedCellWidth = ((MAX_PAGE_WIDTH - MAGIC_NUMBER - totalCustomWidth) / colsWithoutCustomWidth)
        * tableBordersWidthSubtractionMultiplier;

  // Cell width should have some value and not be negative
  return calculatedCellWidth <= 0 ? MINIMUM_CELL_WIDTH : calculatedCellWidth;
} // calcCellAutoWidth()

/**
 * Calculate merged cell width.
 * Return cell auto-width if it isn't merged.
 *
 * @param {object} cell
 * @param {(number | '*')[]} tableWidths
 * @param {number} cellAutoWidth
 * @param {number} colIdx
 * @return {number}
 */
export function calcMergedCellWidth(cell, tableWidths, cellAutoWidth, colIdx) {
  const cellMergedColumnsQuantity = cell.colSpan || 1;
  if (cellMergedColumnsQuantity === 1) {
    // cell is not merged, return rendered cell auto-width
    return cellAutoWidth;
  }

  let colsWithAutoWidth = 0;
  let cellHandled = 0;

  const customWidth = tableWidths.reduce((acc, colWidth, colWidthIdx) => {
    if (colWidthIdx >= colIdx && cellHandled < cellMergedColumnsQuantity) {
      cellHandled += 1;

      if (colWidth === '*') {
        colsWithAutoWidth += 1;
        return acc;
      }

      return acc + colWidth;
    }

    return acc;
  }, 0);

  const autoWidth = cellAutoWidth * colsWithAutoWidth;

  return autoWidth + customWidth;
} // calcMergedCellWidth()

/**
 * Fix table images width to fit table column width and returns a new pdf page content object.
 *
 * @param {object} pdfPageContent
 * @param {string} keyWord
 * @param {number} index
 * @param {boolean} noCellMargins - whether to consider cell margins in width calculations
 * @return {object}
 */
function fixPdfTableContent(pdfPageContent, keyWord, index, noCellMargins = false) {
  const table = { ...pdfPageContent.table };

  const firstRow = pdfPageContent.table?.body?.[0];
  const hasHeader = firstRow && firstRow.some((col) => col.nodeName === 'TH');
  const doesTableHasHeaderRow = !!hasHeader;

  const elementDefaultWidth = calcCellAutoWidth(table.widths, noCellMargins);
  const tableBordersWidthSubtractionMultiplier = INITIAL_MULTIPLIER_VALUE
        - (WIDTH_OF_TABLE_BORDER * (table.widths.length - TABLE_COLS_DEFAULT_QUANTITY));

  table.body = table.body.map((row) => row.map((originalCol, colIndex) => {
    const colWidth = table.widths[colIndex];
    const doesColHasCustomWidth = Number.isFinite(colWidth);
    const colWidthWithoutBorders = colWidth * tableBordersWidthSubtractionMultiplier;

    const mergedCellWidth = calcMergedCellWidth(originalCol, table.widths, elementDefaultWidth, colIndex);
    const isColMerged = mergedCellWidth !== elementDefaultWidth;

    const imagesFinalWidth = (doesColHasCustomWidth && !isColMerged) ? colWidthWithoutBorders : mergedCellWidth;
    const otherElementsFinalWidth = doesColHasCustomWidth ? colWidthWithoutBorders : elementDefaultWidth;

    if (Array.isArray(originalCol.stack) && originalCol.stack.length > 0) {
      const stack = originalCol.stack.map((originalElem) => {
        const element = {
          // eslint-disable-next-line no-use-before-define
          ...getFixedPdfPageContent(originalElem, keyWord, index, noCellMargins),
          minWidth: 0,
          maxWidth: otherElementsFinalWidth,
        };
        if (element.nodeName === 'IMG') {
          element.maxWidth = imagesFinalWidth;
        }
        return element;
      });

      // Fix disappeared images placed in table header rows that are merged vertically
      const colStackHasImage = originalCol.stack.some((el) => (el.nodeName === 'IMG'));
      if (colStackHasImage && originalCol.nodeName === 'TH' && originalCol.rowSpan > 1) {
        return {
          ...originalCol,
          stack,
          fillColor: '#000',
          fillOpacity: MERGED_HEADER_CELL_FILL_OPACITY,
        };
      }
      return { ...originalCol, stack };
    }
    return originalCol;
  }));

  // Prevent breaking rows if it has image in any of the cells (to prevent image cut in half on page break)
  const tableHasImage = table.body.some(
    (row) => row.some(
      (col, colIndex) => (
        col.stack?.some((el) => (el.nodeName === 'IMG')) ?? false
      ),
    ),
  );
  if (tableHasImage) {
    table.dontBreakRows = true;
    table.headerRows = doesTableHasHeaderRow ? 1 : null;
    table.keepWithHeaderRows = doesTableHasHeaderRow;
  }
  table.widths = table.widths.map(
    (width) => (width === '*'
      ? elementDefaultWidth
      : width * tableBordersWidthSubtractionMultiplier),
  );

  return { ...pdfPageContent, table };
} // fixPdfTableContent()

function replaceFailedImagesWithTag(html) {
  // Remove failed/unsupported images
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgs = doc.querySelectorAll('img');
  imgs.forEach((img) => {
    if (img.getAttribute('failtofetch') === 'true' || img.getAttribute('unsupportedimagetype') === 'true') {
      img.remove();
    }
  });
  return doc.body.innerHTML;
} // handleImage()

/**
 * Returns the row (in raw HTML) that has the most TH/TD cells, plus the cell count.
 * Used to normalize colgroup/cell widths for tables coming out of tiptap.
 *
 * @param {Array<string>} tableRows
 * @return {{ colsQuantity: number, rowWithMaximumColumnsNumber: string }}
 */
function getRowWithMaximumColumnsNumber(tableRows) {
  let colsQuantity = 0;
  let rowWithMaximumColumnsNumber = '';

  tableRows.forEach((row) => {
    const counter = Array.from(row.matchAll(/<(?:th|td)\b[^>]*>/gi))
      .reduce((sum, [cellTag]) => {
        const span = Number(/colspan="(\d+)"/i.exec(cellTag)?.[1] ?? 1);
        return sum + span;
      }, 0);
    if (counter > colsQuantity) {
      colsQuantity = counter;
      rowWithMaximumColumnsNumber = row;
    }
  });

  return { colsQuantity, rowWithMaximumColumnsNumber };
} // getRowWithMaximumColumnsNumber()

/**
 * Strips tables whose content is effectively empty (no real cells / no text).
 * pdfmake throws on tables like:
 *   <table style="width: 0px"><colgroup></colgroup><tbody><tr></tr></tbody></table>
 *
 * @param {string} htmlText
 * @return {string}
 */
function removeEmptyTables(htmlText) {
  const tablePattern = /<table\b[^>]*>(.*?)<\/table>/gis;

  let updatedHtmlText = htmlText;
  let match = tablePattern.exec(htmlText);

  while (match !== null) {
    const tableContent = match[1];
    const isTableEmpty = tableContent
      .replace(/<colgroup>.*?<\/colgroup>/gis, '')
      .replace(/<tr>\s*<td>\s*<\/td>\s*<\/tr>/gis, '')
      .replace(/<(?!img\b)[^>]*>/g, '')
      .trim() === '';

    if (isTableEmpty) {
      updatedHtmlText = updatedHtmlText.replace(match[0], '');
    }
    match = tablePattern.exec(htmlText);
  }
  return updatedHtmlText;
} // removeEmptyTables()

/**
 * Normalizes tiptap-emitted table HTML so html-to-pdfmake can size it correctly:
 *  - removes empty tables (pdfmake crashes on them),
 *  - adds an inline width style to each <table>,
 *  - converts custom `colwidth` attributes on cells to `style="width: Npx"`,
 *  - optionally scales pixel widths by `pdfFactor` to fit the PDF page.
 *
 * @param {string} templateText
 * @param {boolean} isPdfExporting - true when the output will be rendered to PDF.
 * @return {string}
 */
function fixTemplateTables(templateText, isPdfExporting = false) {
  const colwidthPattern = /colwidth="(.+?)"/gi;
  const getColumnPattern = /<(?:th|td)\b[^>]*>/gi;
  const searchTablePattern = /<table\b[^>]*>/gi;
  const hasTables = templateText.search(searchTablePattern) !== -1;
  const pdfFactor = 0.635; // multiplier we use to fit table in pdf page size

  if (!hasTables) {
    return templateText;
  }

  const templateTextWithoutEmptyTables = removeEmptyTables(templateText);
  const tables = templateTextWithoutEmptyTables.split('</table>');

  tables.forEach((table, index) => {
    if (!table) {
      return;
    }
    const hasColgroup = /<colgroup\b[^>]*>/i.test(table);

    const rows = table.split('</tr>');
    const { colsQuantity, rowWithMaximumColumnsNumber } = getRowWithMaximumColumnsNumber(rows);
    let tableWidth = 0;
    let colgroupTag = '<colgroup>';
    let colsWithWidth = 0;

    let match = getColumnPattern.exec(rowWithMaximumColumnsNumber);
    while (match) {
      const getColspanPattern = /colspan="(.+?)"/gi;
      const getColwidthPattern = /colwidth="(.+?)"/gi;
      const colspanMatch = getColspanPattern.exec(match[0]);
      const colwidthMatch = getColwidthPattern.exec(match[0]);
      const spanNumber = parseInt(colspanMatch?.[1] ?? 1, 10);
      const colWidth = parseInt(colwidthMatch?.[1] ?? 0, 10);
      const isMissingColumnWidth = colWidth === 0;

      // For merged cells, expand colgroup so it still has one <col> per real column.
      const widths = colwidthMatch?.[1].split(',');
      for (let i = 0; i < spanNumber; i += 1) {
        colgroupTag += widths?.[i] && widths?.[i] !== '0'
          ? `<col style="width: ${widths[i]}px;">`
          : '<col>';
      }

      if (!isMissingColumnWidth) {
        tableWidth += colWidth;
        colsWithWidth += 1;
      }

      match = getColumnPattern.exec(rowWithMaximumColumnsNumber);
    }

    colgroupTag += '</colgroup>';

    if (isPdfExporting) {
      tableWidth *= pdfFactor;
    }

    const styleName = (colsWithWidth === colsQuantity) ? 'width' : 'min-width';
    tables[index] = tables[index]
      .replace(
        searchTablePattern,
        // eslint-disable-next-line max-len
        `<table style="${styleName}: ${Math.round(tableWidth)}px; border: 1px solid #e0e0e0; border-radius: unset; margin-bottom: 10px;">${hasColgroup ? '' : colgroupTag}`,
      );
  });

  return tables.join('</table>')
    .replace(colwidthPattern, (_attr, value) => {
      if (String(value).includes(',') || parseInt(value, 10) === 0) {
        return '';
      }
      return isPdfExporting
        ? `style="width: ${Math.round(value * pdfFactor)}px"`
        : `style="width: ${value}px"`;
    });
} // fixTemplateTables()

/**
 * Draws a checkbox SVG for PDF export (bundled fonts lack checkbox glyphs).
 *
 * @param {boolean} isChecked
 * @return {string}
 */
function taskCheckboxSvg(isChecked) {
  const tick = isChecked
    ? '<polyline points="1.7,4.3 3.3,6 6.3,2.2" fill="none" stroke="#000000" stroke-width="1.1"'
      + ' stroke-linecap="round" stroke-linejoin="round"/>'
    : '';

  return `<svg width="${TASK_CHECKBOX_SIZE}" height="${TASK_CHECKBOX_SIZE}" viewBox="0 0 8 8">`
    + '<rect x="0.5" y="0.5" width="7" height="7" rx="1" fill="none" stroke="#000000" stroke-width="0.8"/>'
    + `${tick}</svg>`;
} // taskCheckboxSvg()

/**
 * @param {Element} listItem
 * @return {boolean}
 */
function isTaskItemChecked(listItem) {
  if (listItem.hasAttribute('data-checked')) {
    return listItem.getAttribute('data-checked') === 'true';
  }
  // Fallback for content pasted from outside the editor, which may carry only the input's state.
  const checkbox = listItem.querySelector('input[type="checkbox"]');
  return !!checkbox && checkbox.hasAttribute('checked');
} // isTaskItemChecked()

/**
 * TextAlign styles the item's paragraphs, not the item, so the first paragraph decides where the box goes.
 *
 * @param {Element} listItem
 * @return {string}
 */
function getTaskItemAlignment(listItem) {
  return listItem.querySelector(':scope > div > :first-child')?.style.textAlign ?? '';
} // getTaskItemAlignment()

/**
 * @param {Document} doc
 * @return {Element} an empty star column that soaks up free width to push the row horizontally
 */
function createRowSpacer(doc) {
  const spacer = doc.createElement('div');
  spacer.setAttribute('data-pdfmake', JSON.stringify({ width: '*' }));
  return spacer;
} // createRowSpacer()

/**
 * Lays a marker and its content out as a pdfmake columns row. pdfmake has no way to align a group of
 * columns, so a centered or right-aligned row shrinks the content to fit and lets star spacers take up
 * the free width, which carries the marker along with the text.
 *
 * @param {Document} doc
 * @param {Element} markerColumn
 * @param {Element} contentColumn
 * @param {string} alignment - the CSS text-align of the content's first paragraph
 * @param {number} columnGap
 * @return {Element}
 */
function createMarkerRow(doc, markerColumn, contentColumn, alignment, columnGap) {
  const row = doc.createElement('div');
  row.setAttribute('data-pdfmake-type', 'columns');
  row.setAttribute('data-pdfmake', JSON.stringify({ columnGap }));

  const isCentered = alignment === 'center';
  const isRight = alignment === 'right';
  const isShiftedRow = isCentered || isRight;
  contentColumn.setAttribute('data-pdfmake', JSON.stringify({ width: isShiftedRow ? 'auto' : '*' }));

  if (isCentered) {
    row.append(createRowSpacer(doc), markerColumn, contentColumn, createRowSpacer(doc));
  } else if (isRight) {
    row.append(createRowSpacer(doc), markerColumn, contentColumn);
  } else {
    row.append(markerColumn, contentColumn);
  }

  return row;
} // createMarkerRow()

/**
 * Turns empty paragraphs into line breaks, because html-to-pdfmake gives an empty paragraph no height.
 * Matches `<p>` with or without attributes, both `<p dir="auto"></p>` and `<p></p>`.
 *
 * @param {string} html
 * @return {string}
 */
export function fixEmptyParagraphs(html) {
  return html.replace(/<p(?:\s[^>]*)?><\/p>/g, '<br/>');
} // fixEmptyParagraphs()

/**
 * Converts Tiptap task lists into pdfmake two-column layout [drawn SVG checkbox, content] and removes bullet dots.
 *
 * @param {string} html
 * @return {string}
 */
export function fixTaskLists(html) {
  if (!html.includes('data-type="taskList"')) {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const taskLists = doc.querySelectorAll('ul[data-type="taskList"]');

  if (taskLists.length === 0) {
    return html;
  }

  taskLists.forEach((taskList) => {
    taskList.setAttribute('data-pdfmake', JSON.stringify({ type: 'none' }));

    Array.from(taskList.children)
      .filter((child) => child.tagName === 'LI')
      .forEach((listItem) => {
        const isChecked = isTaskItemChecked(listItem);
        const alignment = getTaskItemAlignment(listItem);

        Array.from(listItem.children)
          .filter((child) => child.matches('label, input[type="checkbox"]'))
          .forEach((node) => node.remove());

        const boxColumn = doc.createElement('div');
        boxColumn.setAttribute('data-pdfmake', JSON.stringify({
          width: TASK_CHECKBOX_COLUMN_WIDTH,
          margin: [0, TASK_CHECKBOX_TOP_OFFSET, 0, 0],
        }));
        boxColumn.innerHTML = taskCheckboxSvg(isChecked);

        const contentColumn = doc.createElement('div');
        // Move nodes rather than innerHTML so a nested checklist stays a live element and gets
        // rewritten on its own iteration.
        while (listItem.firstChild) {
          contentColumn.appendChild(listItem.firstChild);
        }

        listItem.appendChild(createMarkerRow(doc, boxColumn, contentColumn, alignment, TASK_CHECKBOX_GAP));
      });
  });

  return doc.body.innerHTML;
} // fixTaskLists()

/**
 * @param {Element} listItem - an item of an <ol>
 * @return {string} the number pdfmake would have drawn, with its separator
 */
function getOrderedListMarker(listItem) {
  const list = listItem.parentElement;
  const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
  const isReversed = list.hasAttribute('reversed');
  const start = Number.parseInt(list.getAttribute('start'), 10);
  const defaultStart = isReversed ? items.length : 1;
  const first = Number.isNaN(start) ? defaultStart : start;
  const index = items.indexOf(listItem);

  return `${isReversed ? first - index : first + index}.`;
} // getOrderedListMarker()

/**
 * Allow pdfmake to align list items `ol` or `ul` by center or right. pdfmake always draws the marker at
 * the line start, so an aligned item hides it and draws its own beside the text. Only the first paragraph
 * moves; the rest of the item stays below.
 *
 * @param {string} html
 * @return {string}
 */
export function fixAlignedListItems(html) {
  if (!/text-align:\s*(center|right)/.test(html)) {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const alignmentOf = (listItem) => {
    const firstBlock = listItem.firstElementChild;
    return firstBlock?.tagName === 'P' ? firstBlock.style.textAlign : '';
  };
  const alignedItems = Array.from(doc.querySelectorAll('ul:not([data-type="taskList"]) > li, ol > li'))
    .filter((listItem) => ['center', 'right'].includes(alignmentOf(listItem)));

  // Centered paragraphs outside lists are common; leave those notes exactly as they came in.
  if (alignedItems.length === 0) {
    return html;
  }

  alignedItems.forEach((listItem) => {
    const firstBlock = listItem.firstElementChild;
    const alignment = alignmentOf(listItem);

    // pdfmake still counts a hidden item, so the numbers of the items around it are unaffected.
    listItem.setAttribute('data-pdfmake', JSON.stringify({ listType: 'none' }));

    const isOrdered = listItem.parentElement.tagName === 'OL';
    const markerColumn = doc.createElement('div');
    if (isOrdered) {
      markerColumn.setAttribute('data-pdfmake', JSON.stringify({ width: 'auto' }));
      markerColumn.textContent = getOrderedListMarker(listItem);
    } else {
      markerColumn.setAttribute('data-pdfmake', JSON.stringify({
        width: LIST_BULLET_SIZE,
        margin: [0, LIST_BULLET_TOP_OFFSET, 0, 0],
      }));
      markerColumn.innerHTML = `<svg width="${LIST_BULLET_SIZE}" height="${LIST_BULLET_SIZE}" viewBox="0 0 2 2">`
        + '<circle cx="1" cy="1" r="1" fill="#000000"/></svg>';
    }

    const contentColumn = doc.createElement('div');
    contentColumn.appendChild(firstBlock);

    const gap = isOrdered ? LIST_NUMBER_GAP : LIST_BULLET_GAP;
    listItem.prepend(createMarkerRow(doc, markerColumn, contentColumn, alignment, gap));
  });

  return doc.body.innerHTML;
} // fixAlignedListItems()

/**
 * Clears HTML string from inline row breaks and some inline styles
 *
 * @param {string} html
 * @return {string}
 */
export function clearUnnecessaryHtmlParts(html) {
  // Tiptap emits tables with <colgroup>, <col>, and `colwidth` attributes that
  // html-to-pdfmake's tableAutoSize can't read. Normalize them first (and scale
  // pixel widths to fit the PDF page) so the resulting pdfmake table has matching
  // widths and body — otherwise pdfmake silently drops the table from the output.
  let updatedHtml = fixTemplateTables(html, true)
    .replace(/(\r\n|\n\r|\r|\n)/g, '<br />')
  // replace inline "width" style with any string property
    .replace(/width:(.)?[a-zA-Z-]+;?/g, '')
    .replace(
      /font-family:(.*?)([;"])/g,
      (match, font, lastSign) => (lastSign === '"' ? lastSign : ''),
    ) // replace custom inline fonts
    .replace(
      /text-decoration: none([;"])/g,
      (match, lastSign) => (lastSign === '"' ? lastSign : ''),
    );

  updatedHtml = replaceFailedImagesWithTag(updatedHtml);

  // Placed after fixMergedCells (DOMPurify) so generated data-pdfmake attributes are not stripped by the sanitizer.
  return fixAlignedListItems(fixTaskLists(fixMergedCells(updatedHtml)));
} // clearUnnecessaryHtmlParts()

/**
 * Fixes pdfPage.content image reference name and images width within table and tables formatting
 *
 * @param {Object} pdfPageContent
 * @param {string} keyWord
 * @param {number} index
 * @returns {Object} new pdfPage.content object
 */
export function getFixedPdfPageContent(pdfPageContent, keyWord, index, noCellMargins = false) {
  if (typeof pdfPageContent !== 'object' || pdfPageContent === null) {
    return pdfPageContent;
  }

  // @todo Handle Arabic support globally
  // // if elem has text and text has Arabic language change alignment
  // if ((get(pdfPageContentCopy, 'text') && Arabic.isPrevailInString(this.getTextFromNode(pdfPageContentCopy)))) {
  //   pdfPageContentCopy.direction = 'rtl';
  // }

  // if elem is <img>
  if (pdfPageContent.nodeName === 'IMG') {
    return {
      ...pdfPageContent,
      image: `${pdfPageContent.image}-${keyWord}-${index}`,
      width: pdfPageContent.width * PDF_SCALE_FACTOR,
    };
  }

  let pdfPageContentCopy = { ...pdfPageContent };
  if (pdfPageContent.nodeName === 'P' && pdfPageContent.text === '') {
    pdfPageContentCopy.text = ' ';
  }

  // if elem has a sub-stack
  if (Array.isArray(pdfPageContent.stack) && pdfPageContent.stack.length) {
    pdfPageContentCopy.stack = pdfPageContent.stack.map(
      (stackItem) => getFixedPdfPageContent(stackItem, keyWord, index, noCellMargins),
    );
  }

  // // if elem is <ul>
  if (Array.isArray(pdfPageContent.ul) && pdfPageContent.ul.length) {
    pdfPageContentCopy.ul = pdfPageContent.ul.map(
      (ul) => getFixedPdfPageContent(ul, keyWord, index, noCellMargins),
    );
  }

  // // if elem is <ol>
  if (Array.isArray(pdfPageContent.ol) && pdfPageContent.ol.length) {
    pdfPageContentCopy.ol = pdfPageContent.ol.map(
      (ol) => getFixedPdfPageContent(ol, keyWord, index, noCellMargins),
    );
  }

  // Recurse into columns so images inside checklist rows get unique reference suffixes for pdfmake.
  if (Array.isArray(pdfPageContent.columns) && pdfPageContent.columns.length) {
    pdfPageContentCopy.columns = pdfPageContent.columns.map(
      (column) => getFixedPdfPageContent(column, keyWord, index, noCellMargins),
    );
  }

  // // fix all tables widths and content - for tables with width defined and non-empty body
  if (pdfPageContent.table?.widths
    && Array.isArray(pdfPageContent.table.widths)
    && (pdfPageContent.table?.body?.length ?? 0) > 0) {
    pdfPageContentCopy = fixTableWidths(pdfPageContentCopy);
    pdfPageContentCopy = fixPdfTableContent(pdfPageContentCopy, keyWord, index, noCellMargins);
  }

  //   // element is broken <table> with empty body - add empty row to make it invisible (to look like on the preview)
  if (typeof pdfPageContentCopy.table === 'object' && (pdfPageContent.table?.body?.length ?? 0) === 0) {
    pdfPageContentCopy = {
      ...pdfPageContentCopy,
      table: {
        ...pdfPageContentCopy.table,
        body: [[]],
      },
    };
  }

  return pdfPageContentCopy;
} // getFixedPdfPageContent()

/**
 * Safely converts HTML to PDFMake format by sanitizing the HTML and fixing image references
 * to be unique.
 * @param {String} html
 * @param {String} keyword
 * @param {Number} idx
 * @returns {Object} PDFMake compatible content and images
 */
export function safeHtmlToPdfMake(html, keyword, idx, noCellMargins = false) {
  const pdfMakeResult = htmlToPdfMake(
    clearUnnecessaryHtmlParts(
      html,
    ),
    {
      imagesByReference: true,
      tableAutoSize: true,
      defaultStyles: DEFAULT_PDF_STYLES,
    },
  );

  const rawContent = Array.isArray(pdfMakeResult.content)
    ? pdfMakeResult.content
    : [pdfMakeResult.content];

  const content = rawContent
    .map((pdfPageContent) => getFixedPdfPageContent(pdfPageContent, keyword, idx, noCellMargins));

  const images = Object.keys(pdfMakeResult.images || {}).reduce((acc, key) => {
    acc[`${key}-${keyword}-${idx}`] = pdfMakeResult.images[key];
    return acc;
  }, {});

  return { content, images };
} // safeHtmlToPdfMake()

/**
 * Fixes a PdfMake Image reference by ensuring it follows the correct format.
 * @param {String} reference PdfMake Image reference
 * @returns {String} Fixed PdfMake Image reference
 */
function fixImageReference(reference) {
  if (!reference || typeof reference !== 'string') {
    return '';
  }

  return reference
    .split('-')
    .map((part, idx) => (
      idx === 0
        // first part of image reference should only leave its last character
        ? part.charAt(part.length - 1)
        : part
    ))
    .join('-');
} // fixImageReference()

/**
 * Replaces image element references in a PDFMake document.
 * @param {Array} elements PDFMake document elements
 * @param {Object} replaceWith Replacement mapping
 * @returns {Object} Updated PDFMake document elements
 */
export function replaceImageElementsRefs(elements, replaceWith) {
  return elements.map((element) => {
    const elementCopy = { ...element };

    if (Array.isArray(elementCopy?.stack) && elementCopy?.stack.length > 0) {
      elementCopy.stack = replaceImageElementsRefs(elementCopy.stack, replaceWith);
    }

    // Checklist rows are `columns` nodes; an image inside one is otherwise never reached.
    if (Array.isArray(elementCopy?.columns) && elementCopy?.columns.length > 0) {
      elementCopy.columns = replaceImageElementsRefs(elementCopy.columns, replaceWith);
    }

    if (typeof elementCopy === 'object' && elementCopy.nodeName === 'IMG' && elementCopy.image) {
      const imageNodeReference = fixImageReference(elementCopy.image);
      // Vanilla JS replacement for lodash chain/mapValues/invert
      const replaceImagesFixedMap = Object.entries(replaceWith)
        .reduce((acc, [key, val]) => {
          acc[fixImageReference(key)] = key;
          return acc;
        }, {});

      elementCopy.image = replaceImagesFixedMap[imageNodeReference] || elementCopy.image;
    }

    if (
      elementCopy?.nodeName === 'TABLE'
          && Array.isArray(elementCopy?.table?.body)
          && elementCopy.table.body.length > 0
    ) {
      elementCopy.table.body = elementCopy.table.body.map((row) => {
        return replaceImageElementsRefs(row, replaceWith);
      });
    }

    return elementCopy;
  });
} // replaceImageElementsRefs()

/**
 * Predicts height of a PDF element based on its content and images.
 *
 * Note: Replaces ProgressReport.vue's predictPdfElementHeight() and likely also SessionExportMixin's version.
 *
 * @param {Array} pdfContent pdfMake content
 * @param {Object} pageHeaderImages images used in page header/footer
 * @returns
 */
export async function predictPdfElementHeight(pdfContent, pageHeaderImages) {
  const imageDimensionActions = Object.keys(pageHeaderImages)
    .filter((imageRef) => !pageHeaderImages[imageRef].includes('base64'))
    .map(async (imageRef) => {
      const image = pageHeaderImages[imageRef];
      const dimensions = await getImageDimensions(image);
      return [imageRef, dimensions];
    });

  const imageDimensionsArr = await Promise.all(imageDimensionActions);
  const imageDimensions = imageDimensionsArr.reduce((acc, [imageRef, dimensions]) => {
    acc[imageRef] = dimensions;
    return acc;
  }, {});

  const traverseElements = (items, calc) => items
    ?.map((item) => {
      if (!item?.nodeName) {
      // html-to-pdfmake strips nodeName from <li> with block children (like checklist rows).
      // Calculate stack height instead of returning 0 to prevent header overflow.
        return Array.isArray(item?.stack) ? traverseElements(item.stack, 'sum') : 0;
      }

      let margin = 0;
      if (Array.isArray(item?.margin)) {
        if (item.margin.length === 4) {
          margin = (item.margin[1] || 0) + (item.margin[3] || 0);
        } else if (item.margin.length === 2) {
          margin = (item.margin[1] || 0) * 2;
        } else {
          margin = 0;
        }
      } else {
        margin = item?.margin || 0;
      }

      if (['P'].includes(item.nodeName)) {
        return 13 + margin;
      }
      if (['IMG'].includes(item.nodeName)) {
        if (item.width) {
          const aspectRato = (imageDimensions?.[item.image]?.height ?? 1)
                / (imageDimensions?.[item.image]?.width ?? 1);
          return item.width * aspectRato + margin;
        }
        return (imageDimensions?.[item.image]?.height ?? 0) + margin;
      }
      if (['TH', 'TD', 'LI'].includes(item.nodeName)) {
        return traverseElements(item?.stack || [], 'sum') + margin;
      }
      if (['TABLE'].includes(item.nodeName)) {
        return (item?.table?.body || [])
          .map((row) => traverseElements(row, 'max'))
          .reduce((acc, val) => acc + val, 0) + margin;
      }
      if (['OL'].includes(item.nodeName)) {
        return traverseElements(item.ol, 'sum') + margin;
      }
      if (['UL'].includes(item.nodeName)) {
        return traverseElements(item.ul, 'sum') + margin;
      }
      if (['SVG'].includes(item.nodeName)) {
        return TASK_CHECKBOX_SIZE + margin;
      }
      if (Array.isArray(item.columns) && item.columns.length) {
        // Row height equals the tallest column's content height.
        return Math.max(
          0,
          ...item.columns.map((column) => traverseElements(column.stack ?? [column], 'sum')),
        ) + margin;
      }

      return margin;
    })
    .reduce((acc, val) => {
      if (calc === 'sum') {
        return acc + val;
      }
      if (calc === 'max') {
        return Math.max(acc, val);
      }
      return acc;
    }, 0);
  return traverseElements(pdfContent, 'sum');
} // predictPdfElementHeight()

/**
 * Removes borders from pdfMake table elements within a stack item or array of stack items.
 * @param {Object|Array} stackItem pdfMake compatible stack item or array of stack items
 * @returns {Object|Array}
 */
export function removePdfTableBorders(stackItem) {
  if (Array.isArray(stackItem)) {
    return stackItem.map((item) => removePdfTableBorders(item));
  }
  if (stackItem.table) {
    return {
      ...stackItem,
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        hLineColor: () => 'white',
        vLineColor: () => 'white',
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
    };
  }
  return stackItem;
} // removePdfTableBorders()
